/**
 * Framework-agnostic server-reachability store.
 *
 * The axios client (`client.ts`) sets the status from real request outcomes:
 * - `online`       — a request succeeded (or failed with an HTTP response, which
 *                    still proves the server is reachable).
 * - `reconnecting` — a network error is being retried / failed over in the
 *                    background (e.g. a cold-started server waking up).
 * - `offline`      — retries were exhausted against every configured server.
 *
 * The UI reads it via `useSyncExternalStore` (see the app-level hook), so this
 * module stays React-free to preserve api-client's no-framework contract.
 */

export type ServerReachability = 'online' | 'reconnecting' | 'offline';

let status: ServerReachability = 'online';
const listeners = new Set<() => void>();

export const reachabilityStore = {
  /** Current reachability status (stable reference — a plain string). */
  get(): ServerReachability {
    return status;
  },

  /** Update the status, notifying subscribers only when it actually changes. */
  set(next: ServerReachability): void {
    if (next === status) return;
    status = next;
    listeners.forEach((listener) => listener());
  },

  /** Subscribe to status changes; returns an unsubscribe function. */
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
